"use client"

import {useMemo} from "react"
import {useSidebar} from "@/components/ui/sidebar"
import {Button} from "@/components/ui/button"
import {Clock} from "lucide-react"
import {cn} from "@/lib/utils"
import {gaId} from "@/lib/analytics/clickTracker"
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useShallow} from "zustand/react/shallow";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {UserTrialInfo} from "@/pb/api/devplan/types/user";
import {profileBillingPath} from "@/app/paths";
import Link from "next/link"

export function TrialStatusCard() {
  const {trialInfo, limits} = useBoundStore(useShallow(s => ({trialInfo: s.ownTrialInfo, limits: s.ownLimits})))
  const {state} = useSidebar()
  const expanded = state === 'expanded'
  const collapsed = !expanded

  if (!trialInfo || (!trialInfo.started && !trialInfo.ends) || !limits || trialInfo.skipTrialNotification) {
    return null
  }
  const {active, expired} = trialInfo
  if (!active && !limits.forcedReadonly) {
    // We are showing trial card only if user is on trial or functionality limited.
    return null
  }
  if (collapsed) {
    return <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex h-12 w-full items-center justify-center">
          <Clock className="text-slate-500 size-6 shrink-0"/>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <TrialHeader expired={expired}/>
        <TrialMessage expired={expired} trialInfo={trialInfo}/>
      </TooltipContent>
    </Tooltip>
  }

  return <div className={cn(
    "w-full p-3 rounded-lg bg-accent border space-y-2",
    collapsed ? "p-2" : "p-3"
  )}>
    <div>
      <TrialHeader expired={expired}/>
      <TrialMessage expired={expired} trialInfo={trialInfo}/>
    </div>
    <Link className="w-full" href={profileBillingPath()} {...gaId("upgrade_button_sidebar")}>
      <Button size="sm" className="w-full">Upgrade</Button>
    </Link>
  </div>
}

function TrialHeader({expired}: { expired: boolean }) {
  return <span className="text-sm font-medium">{expired ? "Your free trial has expired" : "Trial Active"}</span>
}

function TrialMessage({trialInfo, expired}: { expired: boolean, trialInfo: UserTrialInfo }) {
  const daysRemaining = useMemo(() => {
    if (!trialInfo.active || !trialInfo.ends) {
      return 0
    }
    const {ends} = trialInfo
    const now = new Date()
    const diffTime = ends.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  }, [trialInfo])
  if (expired) {
    return <p className="text-sm">
      Upgrade to continue access your projects.
    </p>
  }
  return <p className="text-sm">
    You have {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in your free trial.
  </p>
}